import { useEffect, useRef, useState } from "react";
import Sidebar from "./sidebar";
import { ScrollPanel } from "primereact/scrollpanel";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router-dom";
import {
  AiOutlineAppstore,
  AiOutlineDashboard,
  AiOutlinePlus,
  AiOutlineEdit,
} from "react-icons/ai";
import Navbar from "./navbar";
import {
  MdCurrencyExchange,
  MdOutlineCategory,
  MdOutlineManageAccounts,
  MdShoppingCartCheckout,
  MdDashboard,
  MdInventory,
  MdPeople,
  MdStore,
  MdReceipt,
  MdQuestionAnswer,
  MdDescription,
  MdPolicy,
  MdChat,
  MdReviews,
  MdChecklist,
  MdSettings,
  MdLocalOffer,
  MdWarehouse,
} from "react-icons/md";
import { IoMdSettings, IoMdHome } from "react-icons/io";
import { 
  BiCategoryAlt,
  BiCategory,
  BiUserPlus,
  BiEdit,
  BiShoppingBag,
  BiPurchaseTag,
  BiHelpCircle,
  BiFileBlank,
  BiShield,
  BiMessageRounded,
  BiStar,
  BiCheckSquare,
  BiCog,
  BiGift
} from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { RESET_ERROR, RESET_SUCCESS } from "../store/Types/AuthTypes";
import { toast } from "react-toastify";
import { getEnableCurrency } from "../store/AsyncMethods/CurrencyMethod";
import { BsFillPersonLinesFill, BsPeople, BsBuilding } from "react-icons/bs";
import { FaFileContract, FaFileAlt, FaShoppingCart, FaComments, FaRegComments, FaPuzzlePiece } from "react-icons/fa";
import { adminSocketService } from "../services/adminSocketService";
import {
  addMessageToAction,
  setConversationsAction,
  updateConversationLatestMessageAction,
} from "../store/Actions/AdminChatActions";
import notificationSound from "../assets/sounds/notification.mp3";

function RootLayout({ children }) {
  const { activeConversationUserId } = useSelector(
    (state) => state.AdminChatReducer
  );
  const activeUserIdRef = useRef(null);
  useEffect(() => {
    activeUserIdRef.current = activeConversationUserId;
  }, [activeConversationUserId]);

  let isTabletMid = useMediaQuery({ query: "(max-width: 768px)" });
  const [open, setOpen] = useState(isTabletMid ? false : true);
  const sidebarRef = useRef();
  const { pathname } = useLocation();

  const { success, error, user } = useSelector((state) => state.AuthReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch({ type: RESET_SUCCESS });
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch({ type: RESET_ERROR });
    }
  }, [error]);

  useEffect(() => {
    if (user) {
      dispatch(getEnableCurrency());
    }
  }, []);
  useEffect(() => {
    if (user) {
      adminSocketService.connect();
      adminSocketService.getConversations((response) => {
        if (response.status === "ok") {
          dispatch(setConversationsAction(response.conversations));
        }
      });
      adminSocketService.onNewMessage((newMessage) => {
        const audio = new Audio(notificationSound);
        audio.play();
        dispatch(addMessageToAction(newMessage));
        dispatch(
          updateConversationLatestMessageAction({
            message: newMessage,
            activeUserId: activeUserIdRef.current, // Pass the currently active user ID
          })
        );
        if (activeUserIdRef.current === newMessage.user_id) {
          adminSocketService.markMessagesAsRead({
            user_id: newMessage.user_id,
          });
        }
      });
    }
    return () => {
      adminSocketService.disconnect();
    };
  }, [dispatch, user]);

  const items = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: MdDashboard,
      showInSidebar: true,
    },
    {
      name: "Manage Categories",
      path: "/categories",
      icon: BiCategory,
      showInSidebar: true,
    },
    {
      name: "Add Category",
      path: "/add-categories",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Categories"],
    },
    {
      name: "Edit Category",
      path: "/edit-categories",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Categories"],
    },

    {
      name: "Manage Sub Categories",
      path: "/sub-categories",
      icon: BiCategoryAlt,
      showInSidebar: true,
    },
    {
      name: "Add Sub Category",
      path: "/add-sub-categories",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Sub Categories"],
    },
    {
      name: "Edit Sub Category",
      path: "/edit-sub-categories",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Sub Categories"],
    },
    {
      name: "Manage Products",
      path: "/products",
      icon: BiShoppingBag,
      showInSidebar: true,
    },
    {
      name: "Add Product",
      path: "/add-product",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Products"],
    },
    {
      name: "Edit Product",
      path: "/edit-product",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Products"],
    },
    {
      name: "Manage Users",
      path: "/manage-users",
      icon: MdPeople,
      showInSidebar: true,
    },
    {
      name: "Add User",
      path: "/add-user",
      icon: BiUserPlus,
      showInSidebar: false,
      parents: ["Manage Users"],
    },
    {
      name: "Edit User",
      path: "/edit-user",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Users"],
    },
    {
      name: "Manage Orders",
      path: "/orders",
      icon: MdReceipt,
      showInSidebar: true,
    },
    {
      name: "Add Order",
      path: "/add-order",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Orders"],
    },
    {
      name: "Edit Order",
      path: "/edit-order",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Orders"],
    },
    {
      name: "Manage Warehouses",
      path: "/warehouses",
      icon: MdWarehouse,
      showInSidebar: true,
    },
    {
      name: "Manage Coupons",
      path: "/manage-coupons",
      icon: BiGift,
      showInSidebar: true,
    },
    {
      name: "Manage Currency",
      path: "/manage-currency",
      icon: MdCurrencyExchange,
      showInSidebar: true,
    },
    {
      name: "Add Currency",
      path: "/add-currency",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Currency"],
    },
    {
      name: "Edit Currency",
      path: "/edit-currency",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Currency"],
    },
    {
      name: "Manage Customer",
      path: "/manage-customers",
      icon: BsPeople,
      showInSidebar: true,
    },
    {
      name: "Contact Submissions",
      path: "/contact-submissions",
      icon: MdQuestionAnswer,
      showInSidebar: true,
    },
    {
      name: "Add Customer",
      path: "/add-customers",
      icon: BiUserPlus,
      showInSidebar: false,
      parents: ["Manage Customer"],
    },
    {
      name: "Edit Customer",
      path: "/edit-customer",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Customer"],
    },
    {
      name: "Manage Vendor",
      path: "/manage-vendor",
      icon: BsBuilding,
      showInSidebar: true,
    },
    {
      name: "Add Vendor",
      path: "/add-vendor",
      icon: BiUserPlus,
      showInSidebar: false,
      parents: ["Manage Vendor"],
    },
    {
      name: "Edit Vendor",
      path: "/edit-vendor",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Vendor"],
    },
    {
      name: "Manage Purchase",
      path: "/purchase",
      icon: BiPurchaseTag,
      showInSidebar: true,
    },
    {
      name: "Add Purchase",
      path: "/add-purchase",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Purchase"],
    },
    {
      name: "Edit Purchase",
      path: "/edit-purchase",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Purchase"],
    },
    {
      name: "Manage FAQs",
      path: "/faqs",
      icon: BiHelpCircle,
      showInSidebar: true,
    },
    {
      name: "Add FAQs",
      path: "/add-faqs",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage FAQs"],
    },
    {
      name: "Edit FAQs",
      path: "/edit-faqs",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage FAQs"],
    },
    {
      name: "Manage T&Cs",
      path: "/terms",
      icon: BiFileBlank,
      showInSidebar: true,
    },
    {
      name: "Add Terms and Conditions",
      path: "/add-terms",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage T&Cs"],
    },
    {
      name: "Edit Terms and Conditions",
      path: "/edit-terms",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage T&Cs"],
    },
    {
      name: "Manage Policies",
      path: "/policies",
      icon: BiShield,
      showInSidebar: true,
    },
    {
      name: "Add Policies",
      path: "/add-policies",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Policies"],
    },
    {
      name: "Edit Policies",
      path: "/edit-policies",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Policies"],
    },
    {
      name: "Customer Live Chat",
      path: "/live-chat",
      icon: BiMessageRounded,
      showInSidebar: true,
    },
    {
      name: "Customer Order Reviews",
      path: "/customer-order-reviews",
      icon: BiStar,
      showInSidebar: true,
    },
    {
      name: "Manage Reviews",
      path: "/manage-reviews",
      icon: MdReviews,
      showInSidebar: true,
    },
    {
      name: "Add Review",
      path: "/add-review",
      icon: AiOutlinePlus,
      showInSidebar: false,
      parents: ["Manage Reviews"],
    },
    {
      name: "Edit Review",
      path: "/edit-review",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Reviews"],
    },
    {
      name: "View Review",
      path: "/view-review",
      icon: BiEdit,
      showInSidebar: false,
      parents: ["Manage Reviews"],
    },
    {
      name: "Product Selection",
      path: "/product-selection",
      icon: BiCheckSquare,
      showInSidebar: true,
    },
    {
      name: "Hero Section",
      path: "/hero-section",
      icon: IoMdHome,
      showInSidebar: true,
    },
    {
      name: "Plugins",
      path: "/plugins",
      icon: FaPuzzlePiece,
      showInSidebar: true,
    },
    {
      name: "Meta Ads",
      path: "/plugins/meta-ads",
      icon: FaPuzzlePiece,
      showInSidebar: false,
      parents: ["Plugins"],
    },
  ];
  if (user?.role_id === 1) {
    items.push({
      name: "Setup",
      path: "/site-setup",
      icon: BiCog,
      showInSidebar: true,
    });
    items.push({
      name: "Hero Section",
      path: "/site-setup/hero-section",
      icon: IoMdHome,
      showInSidebar: false,
      parents: ["Setup"],
    });
    items.push({
      name: "Coupons",
      path: "/site-setup/coupons",
      icon: BiGift,
      showInSidebar: false,
      parents: ["Setup"],
    });
    // Add API Docs link for admin users in the sidebar
    if (!items.some((it) => it.path === "/api-docs")) {
      items.push({
        name: "API Docs",
        path: "/api-docs",
        icon: FaFileAlt,
        showInSidebar: true,
      });
    }
  }

  useEffect(() => {
    if (isTabletMid) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isTabletMid]);

  useEffect(() => {
    isTabletMid && setOpen(false);
  }, [pathname]);

  const Nav_animation = isTabletMid
    ? {
        open: {
          x: 0,
          width: "16rem",
          transition: {
            damping: 40,
          },
        },
        closed: {
          x: -250,
          width: 0,
          transition: {
            damping: 40,
            delay: 0.15,
          },
        },
      }
    : {
        open: {
          width: "16rem",
          transition: {
            damping: 40,
          },
        },
        closed: {
          width: "4rem",
          transition: {
            damping: 40,
          },
        },
      };
  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        open={open}
        Nav_animation={Nav_animation}
        sidebarRef={sidebarRef}
        setOpen={(value) => setOpen(value)}
        pathname={pathname}
        isTabletMid={isTabletMid}
        items={items}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar setOpen={(value) => setOpen(value)} items={items} />
        <div className="flex-1 overflow-y-auto">
          <div className="pt-6 mx-auto p-2 md:pr-2">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default RootLayout;
